/**
 * The Tesora theme layer.
 *
 * The design document (`docs/design/Tesora - Desktop.html`) exposes three live
 * tweaks — Theme, Accent and Display font — so that stakeholders can flip
 * through them in a room. The values below are that document's values, not an
 * interpretation of them.
 *
 * Everything the UI paints reads a `--t-*` custom property. Applying a theme is
 * therefore one write of inline custom properties onto the theme root; no
 * component holds a colour, so nothing can render against a stale one.
 */

export const THEME_NAMES = [
  'Ink violet',
  'Espresso',
  'Midnight',
  'Graphite',
  'Oxblood',
  'Daylight',
  'Porcelain',
] as const
export type ThemeName = (typeof THEME_NAMES)[number]

/**
 * Whether a theme paints dark-on-light or light-on-dark.
 *
 * Not cosmetic bookkeeping: it decides the sign of every surface. The five
 * themes the design document ships are all dark, and the surfaces used to be
 * hardcoded white overlays — `rgba(255,255,255,0.07)` — which is invisible on a
 * light background. So a theme now carries its own surfaces rather than
 * inheriting one mode's assumptions.
 */
export type ThemeMode = 'dark' | 'light'

export const ACCENTS = ['#FFD7EF', '#F5C77E', '#9FD8FF', '#C9B8FF', '#E9E4DC'] as const
export type Accent = (typeof ACCENTS)[number]

export const DISPLAY_FONTS = ['Archivo', 'Inter Tight', 'Space Grotesk', 'IBM Plex Sans'] as const
export type DisplayFont = (typeof DISPLAY_FONTS)[number]

interface ThemeSurface {
  readonly mode: ThemeMode
  readonly bg: string
  readonly desk: string
  readonly line: string
  readonly surface: string
  readonly surfaceRaised: string
  readonly text: string
  readonly muted: string
}

/** A dark theme, whose surfaces are the design's white overlays. */
function dark(bg: string, desk: string, line = 'rgba(255,255,255,0.12)'): ThemeSurface {
  return {
    mode: 'dark',
    bg,
    desk,
    line,
    surface: 'rgba(255,255,255,0.07)',
    surfaceRaised: 'rgba(255,255,255,0.06)',
    text: '#FFFFFF',
    muted: 'rgba(255,255,255,0.62)',
  }
}

/**
 * A light theme.
 *
 * `surface` is opaque white rather than a translucent overlay: stacking
 * translucent black on a light background greys everything and loses the card
 * edge the design leans on. `ink` carries through to the text and the muted
 * tone so a light theme reads as one colour family rather than grey-on-white.
 */
function light(bg: string, desk: string, surfaceRaised: string, ink: string): ThemeSurface {
  const rgb = ink
    .replace('#', '')
    .match(/.{2}/g)!
    .map((h) => parseInt(h, 16))
    .join(',')
  return {
    mode: 'light',
    bg,
    desk,
    line: `rgba(${rgb},0.14)`,
    surface: '#FFFFFF',
    surfaceRaised,
    text: ink,
    muted: `rgba(${rgb},0.62)`,
  }
}

const THEMES: Record<ThemeName, ThemeSurface> = {
  // The design document's five. Values unchanged (§6).
  'Ink violet': dark('#100B14', '#080509', 'rgba(255,255,255,0.11)'),
  Espresso: dark('#17120C', '#0C0906'),
  Midnight: dark('#0A0F18', '#05070C'),
  Graphite: dark('#111312', '#080908'),
  Oxblood: dark('#180A0C', '#0C0506'),

  // Light mode. NOT from the design document — it ships five dark themes and
  // no light one — so these are additions rather than transcriptions, and §6's
  // "a hex differing from the design is a bug" does not apply to them. The ink
  // is reused from the dark themes it mirrors so the accent pairing, which is
  // dark-ink-on-pastel in both modes, keeps working untouched.
  Daylight: light('#FBF9F6', '#F1EDE7', '#F6F2EC', '#100B14'),
  Porcelain: light('#F7F8FA', '#EAEDF2', '#F1F3F7', '#0A0F18'),
}

/** Whether the named theme is light. Read by the Appearance control. */
export function themeMode(name: ThemeName): ThemeMode {
  return THEMES[name].mode
}

const FONT_STACKS: Record<DisplayFont, string> = {
  Archivo: 'Archivo, Inter, sans-serif',
  'Inter Tight': "'Inter Tight', Inter, sans-serif",
  'Space Grotesk': "'Space Grotesk', Inter, sans-serif",
  'IBM Plex Sans': "'IBM Plex Sans', Inter, sans-serif",
}

export const DEFAULT_THEME: ThemeName = 'Ink violet'
export const DEFAULT_ACCENT: Accent = '#FFD7EF'
export const DEFAULT_FONT: DisplayFont = 'Archivo'

export interface ThemeSelection {
  readonly theme: ThemeName
  readonly accent: Accent
  readonly font: DisplayFont
}

/** The swatch a theme is picked by in the Appearance control. */
export function themeSwatch(name: ThemeName): string {
  return THEMES[name].bg
}

/**
 * The `--t-*` custom properties for a selection, as a plain record so it can be
 * spread into a React `style` prop or written with `setProperty`.
 */
export function themeVars(sel: ThemeSelection): Record<string, string> {
  const t = THEMES[sel.theme]
  return {
    '--t-bg': t.bg,
    '--t-desk': t.desk,
    '--t-surface': t.surface,
    '--t-surface-raised': t.surfaceRaised,
    '--t-line': t.line,
    '--t-text': t.text,
    '--t-muted': t.muted,
    '--t-accent': sel.accent,
    /*
      THE ACCENT AS INK, which is not the accent (#140).

      Every accent in the design is a PASTEL — it is a fill, with dark ink on
      top, and `--t-on-accent` is the pair for that. Used as a text colour it
      works on a dark surface and is very nearly invisible on a light one:
      `+ 1,820.00 EUR` in Daylight is pale pink on off-white, and money arriving
      was the least readable thing on the screen. Found by looking at it.

      On a light theme the ink is the theme's own text colour. The direction is
      NOT lost by that: `formatMoney` always renders the sign, and the rule that
      "colour alone never carries the direction" is exactly what makes dropping
      the colour here safe. Decoration goes; the fact stays.
    */
    '--t-accent-ink': t.mode === 'light' ? t.text : sel.accent,
    '--t-on-accent': '#100B14',
    '--t-on-accent-muted': 'rgba(16,11,20,0.6)',
    '--t-display': FONT_STACKS[sel.font],
  }
}

const STORAGE_KEY = 'tesora.appearance'

function isTheme(v: unknown): v is ThemeName {
  return typeof v === 'string' && (THEME_NAMES as readonly string[]).includes(v)
}
function isAccent(v: unknown): v is Accent {
  return typeof v === 'string' && (ACCENTS as readonly string[]).includes(v)
}
function isFont(v: unknown): v is DisplayFont {
  return typeof v === 'string' && (DISPLAY_FONTS as readonly string[]).includes(v)
}

/**
 * Read the persisted selection.
 *
 * Every field is validated rather than cast. A stale or hand-edited
 * localStorage value must degrade to the design's default, not paint an
 * undefined custom property and leave the app unreadable.
 */
export function loadSelection(storage: Pick<Storage, 'getItem'> = localStorage): ThemeSelection {
  let raw: string | null = null
  try {
    raw = storage.getItem(STORAGE_KEY)
  } catch {
    // Private-mode / blocked storage. Defaults are correct here.
    return { theme: DEFAULT_THEME, accent: DEFAULT_ACCENT, font: DEFAULT_FONT }
  }
  if (raw === null) return { theme: DEFAULT_THEME, accent: DEFAULT_ACCENT, font: DEFAULT_FONT }

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return { theme: DEFAULT_THEME, accent: DEFAULT_ACCENT, font: DEFAULT_FONT }
  }
  const o = (typeof parsed === 'object' && parsed !== null ? parsed : {}) as Record<string, unknown>
  return {
    theme: isTheme(o.theme) ? o.theme : DEFAULT_THEME,
    accent: isAccent(o.accent) ? o.accent : DEFAULT_ACCENT,
    font: isFont(o.font) ? o.font : DEFAULT_FONT,
  }
}

export function saveSelection(
  sel: ThemeSelection,
  storage: Pick<Storage, 'setItem'> = localStorage,
): void {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(sel))
  } catch {
    // Appearance is a preference, not state. Failing to persist it must never
    // fail the render.
  }
}
